/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Grid,
  CircularProgress
} from '@material-ui/core';
import {
  DeleteOutlineOutlined,
  Check,
  CancelOutlined,
  AddCircleOutline,
  FileCopyOutlined,
  DoubleArrowOutlined
} from '@material-ui/icons';
import { connect } from 'react-redux';
import {
  getUserCPTCodes,
  getSystemCPTCodes,
  addSystemCPTCode,
  deleteUserCPTCode,
  addUserCPTCode
} from 'services/cpt/action';
import {
  getUserICD10Codes,
  getSystemICD10Codes,
  addSystemICD10Code,
  addUserICD10Code,
  deleteUserICD10Code
} from 'services/icd10/action';
import { savePastDiagnosis, saveTempData } from 'services/soap/action';
import schema from './schema';
import validate from 'validate.js';


// ─── Helpers ──────────────────────────────────────────────────────────────────

const findSearchVals = (items, text) => {
  if (!items || !text) return items || [];
  const terms = text.toLowerCase().split(' ').filter(Boolean);
  return items.filter(item =>
    terms.every(term =>
      (item.code && item.code.toLowerCase().includes(term)) ||
      (item.description && item.description.toLowerCase().includes(term))
    )
  );
};


// ─── Component ────────────────────────────────────────────────────────────────

const UserDefined = props => {
  const { classes, isCPT, userID, ehrID, fromLabOrder, isDxUpdate } = props;

  const [loading, setLoading]   = useState(true);
  const [isEdit, setIsEdit]     = useState(false);
  const [selectedVal, setSelectedVal] = useState([]);
  const [searchVal, setSearchVal]     = useState('');

  // Master lists — full unfiltered data
  const [cptUserMaster, setCPTUserMaster]     = useState([]);
  const [icdUserMaster, setICDUserMaster]     = useState([]);
  const [cptSystemMaster, setCPTSystemMaster] = useState([]);
  const [icdSystemMaster, setICDSystemMaster] = useState([]);

  // Display lists — filtered by search
  const [cptCode, setCPTCode] = useState([]);
  const [icdCode, setICDCode] = useState([]);

  const initialUserState = {
    code: '',
    description: '',
    keyid: userID,
    isValid: false,
    touched: {},
    errors: {}
  };
  const [values, setValues] = useState(initialUserState);


  // ── Data loading ─────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        if (isCPT) {
          // FIX: load sequentially so user list is known before deciding
          // whether to fall back to system list for display
          const userRes = await props.getUserCPTCodes(ehrID);
          const userData = userRes?.data?.length > 0 ? userRes.data : [];
          if (!cancelled) {
            setCPTCode(userData);
            setCPTUserMaster(userData);
          }

          const sysRes = await props.getSystemCPTCodes(ehrID);
          const sysData = sysRes?.data?.length > 0 ? sysRes.data : [];
          if (!cancelled) {
            setCPTSystemMaster(sysData);
            // Only use system data for display if user list is empty
            if (userData.length === 0) setCPTCode(sysData);
          }
        } else {
          const userRes = await props.getUserICD10Codes(ehrID);
          const userData = userRes?.data?.length > 0 ? userRes.data : [];
          if (!cancelled) {
            setICDCode(userData);
            setICDUserMaster(userData);
          }

          const sysRes = await props.getSystemICD10Codes(ehrID);
          const sysData = sysRes?.data?.length > 0 ? sysRes.data : [];
          if (!cancelled) {
            setICDSystemMaster(sysData);
            if (userData.length === 0) setICDCode(sysData);
          }
        }
      } catch (err) {
        console.error('Failed to load codes:', err);
      } finally {
        // FIX: always clear loading, even if data was empty or errored
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isCPT, ehrID]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Search / field change ─────────────────────────────────────────────────────

  const handleChange = useCallback((field, value) => {
    if (field === 'searchVal') {
      setSearchVal(value);
      if (isCPT) {
        if (value) {
          // Search user list first, fall back to system list
          let result = findSearchVals(cptUserMaster, value);
          if (result.length === 0) result = findSearchVals(cptSystemMaster, value);
          setCPTCode(result);
        } else {
          setCPTCode(cptUserMaster.length > 0 ? cptUserMaster : cptSystemMaster);
        }
      } else {
        if (value) {
          let result = findSearchVals(icdUserMaster, value);
          if (result.length === 0) result = findSearchVals(icdSystemMaster, value);
          setICDCode(result);
        } else {
          setICDCode(icdUserMaster.length > 0 ? icdUserMaster : icdSystemMaster);
        }
      }
    } else {
      const newState = { ...values, [field]: value, touched: { ...values.touched, [field]: true } };
      const errors = validate(newState, schema);
      newState.errors = errors || {};
      newState.isValid = !errors;
      setValues(newState);
    }
  }, [isCPT, cptUserMaster, cptSystemMaster, icdUserMaster, icdSystemMaster, values]);


  // ── Select an item ─────────────────────────────────────────────────────────────

  const handleSelected = useCallback((value, isSearchVal = false) => {
    setSelectedVal(prev => {
      if (isSearchVal) {
        // FIX: correct duplicate check — filter returns array, check .length
        const isDuplicate = prev.some(c => c.description === value);
        if (isDuplicate) return prev;
        return [...prev, { description: value, codeType: isCPT ? 'cpt' : 'diagnosis' }];
      } else {
        // FIX: was checking `if (!isExist)` where isExist is always an array (truthy)
        const isDuplicate = prev.some(c => c.id === value.id);
        if (isDuplicate) return prev;
        return [...prev, { code: value.code, description: value.description, codeType: isCPT ? 'cpt' : 'diagnosis' }];
      }
    });
  }, [isCPT]);


  // ── Save / submit ──────────────────────────────────────────────────────────────

  const handleListCPTICD = useCallback(async () => {
    debugger
    if (fromLabOrder) {
      props.handleList('labOrderDiagnosis', selectedVal);
    } else if (isDxUpdate) {
      if (isCPT) {
        const res = await props.addUserCPTCode(selectedVal);
        if (res && selectedVal.length > 0) {
          // FIX: use .map() with return value instead of forEach+push
          const newState = selectedVal.map(item => ({ ...item, codeType: 'cpt' }));
          props.handleSaveDiagnosis(newState);
        }
      } else {
        const res = await props.addUserICD10Code(selectedVal);
        if (res && selectedVal.length > 0) {
          const newState = selectedVal.map(item => ({ ...item, codeType: 'diagnosis' }));
          props.handleSaveDiagnosis(newState);
        }
      }
    } else {
      let val;
      if (isCPT) {
        props.handleFieldChange('cptList', selectedVal);
        val = props.getValuesforCopy(false, '', '', '', '', selectedVal);
      } else {
        props.handleFieldChange('diagnosisList', selectedVal);
        val = props.getValuesforCopy(false, '', '', '', '', '', selectedVal);
      }
      props.saveTempData(val);
    }
    props.onCancel();
  }, [fromLabOrder, isDxUpdate, isCPT, selectedVal, props]);


  // ── Add new custom code ────────────────────────────────────────────────────────

  const add = useCallback(async () => {
    if (!values.isValid) return;

    const codes = [{ code: values.code, description: values.description, keyid: values.keyid }];

    try {
      if (isCPT) {
        // FIX: pass ehrID to getUserCPTCodes; only call setIsEdit once at the end
        await props.addUserCPTCode(codes);
        const userRes = await props.getUserCPTCodes(ehrID);
        if (userRes?.data?.length > 0) {
          setCPTUserMaster(userRes.data);
          const filtered = searchVal
            ? findSearchVals(userRes.data, searchVal)
            : userRes.data;
          setCPTCode(filtered);
        }

        await props.addSystemCPTCode(ehrID, codes);
        const sysRes = await props.getSystemCPTCodes(ehrID);
        if (sysRes?.data?.length > 0) setCPTSystemMaster(sysRes.data);
      } else {
        // FIX: pass ehrID to getUserICD10Codes
        await props.addUserICD10Code(codes);
        const userRes = await props.getUserICD10Codes(ehrID);
        if (userRes?.data?.length > 0) {
          setICDUserMaster(userRes.data);
          const filtered = searchVal
            ? findSearchVals(userRes.data, searchVal)
            : userRes.data;
          setICDCode(filtered);
        }

        await props.addSystemICD10Code(ehrID, codes);
        const sysRes = await props.getSystemICD10Codes(ehrID);
        if (sysRes?.data?.length > 0) setICDSystemMaster(sysRes.data);
      }

      // FIX: reset form and close edit row once, after all API calls complete
      setValues(initialUserState);
      setIsEdit(false);
    } catch (err) {
      console.error('Failed to add code:', err);
    }
  }, [values, isCPT, ehrID, searchVal, initialUserState]); // eslint-disable-line react-hooks/exhaustive-deps


  const removeSelectedVal = useCallback(idx => {
    setSelectedVal(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const toggleEdit = useCallback(() => setIsEdit(prev => !prev), []);


  // ── Render ─────────────────────────────────────────────────────────────────────

  const displayCodes = isCPT ? cptCode : icdCode;

  return (
    <Grid container spacing={3}>

      {/* Left panel — code list */}
      <Grid item md={6} xs={12}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10%' }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <div className={classes.templateWrap}>
            <div className={classes.templateBody}>
              <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
                <Table className={classes.templateTable} style={{ overflow: 'hidden' }}>

                  {/* Search + Add/Cancel row */}
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '100%' }}>
                        <TextField
                          className={[classes.textBox, classes.fullWidth]}
                          InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                          onChange={e => handleChange('searchVal', e.target.value)}
                          placeholder="Search user codes and descriptions"
                          value={searchVal}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                        {!isEdit ? (
                          <>
                            {searchVal && displayCodes.length === 0 && (
                              <Button
                                className={classes.addButton}
                                color="primary"
                                onClick={() => handleSelected(searchVal, true)}
                                size="small"
                                style={{ marginRight: 5 }}
                                variant="contained"
                              >
                                <DoubleArrowOutlined />
                              </Button>
                            )}
                            <Button
                              className={classes.addButton}
                              color="primary"
                              onClick={toggleEdit}
                              size="small"
                              variant="contained"
                            >
                              <AddCircleOutline />
                            </Button>
                          </>
                        ) : (
                          <Button
                            className={classes.addButton}
                            color="primary"
                            onClick={toggleEdit}
                            size="small"
                            style={{ backgroundColor: '#B9B9B9' }}
                            variant="contained"
                          >
                            <CancelOutlined />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  {/* Inline add form */}
                  {isEdit && (
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: '100%', whiteSpace: 'nowrap' }}>
                          <TextField
                            className={classes.textBox}
                            InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                            onChange={e => handleChange('code', e.target.value)}
                            placeholder="Input Code"
                            style={{ width: 80 }}
                            value={values.code}
                            variant="outlined"
                          />
                          <TextField
                            className={classes.textBox}
                            InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                            onChange={e => handleChange('description', e.target.value)}
                            placeholder="Input Description"
                            style={{ width: 'calc(100% - 85px)', marginLeft: 5 }}
                            value={values.description}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                          <IconButton
                            color="primary"
                            disabled={!values.isValid}
                            onClick={add}
                            size="small"
                            style={{ marginLeft: -15, color: '#03A484' }}
                          >
                            <Check />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  )}

                  {/* Code list */}
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        style={{ borderColor: 'transparent', height: 'calc(100vh - 400px)' }}
                      >
                        <PerfectScrollbar style={{ height: 'calc(100vh - 400px)' }}>
                          <ul className={classes.cptList}>
                            {displayCodes.length > 0
                              ? displayCodes.map(item => (
                                  <li key={item.id} value={item.id}>
                                    <IconButton
                                      onClick={() => handleSelected(item)}
                                      size="small"
                                      color="primary"
                                    >
                                      <FileCopyOutlined />
                                    </IconButton>
                                    {item.code} {item.description}
                                  </li>
                                ))
                              : null}
                          </ul>
                        </PerfectScrollbar>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </Grid>

      {/* Right panel — selected items */}
      <Grid item md={6} xs={12}>
        <div className={classes.templateWrap}>
          <div className={classes.templateBody}>
            <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
              <Table className={classes.templateTable} style={{ overflow: 'hidden' }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '100%', height: 49 }}>
                      <b>Selected {isCPT ? 'CPT' : 'Diagnosis'}</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell style={{ borderColor: 'transparent', height: 'calc(100vh - 400px)' }}>
                      <PerfectScrollbar style={{ height: 'calc(100vh - 400px)' }}>
                        <ul className={classes.cptListRightIcon}>
                          {/* FIX: key added to list items */}
                          {selectedVal.map((val, idx) => (
                            <li key={`${val.code || val.description}-${idx}`}>
                              {val.code ? `${val.code} ` : ''}
                              {val.description || ''}
                              <IconButton onClick={() => removeSelectedVal(idx)} size="small">
                                <DeleteOutlineOutlined />
                              </IconButton>
                            </li>
                          ))}
                        </ul>
                      </PerfectScrollbar>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </Grid>

      {/* Footer actions */}
      <Grid className={classes.modalFooter} item xs={12}>
        <Button onClick={props.onCancel} size="small" variant="outlined">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={selectedVal.length === 0}
          onClick={handleListCPTICD}
          size="small"
          style={{ margin: '0 10px' }}
          variant="contained"
        >
          Save
        </Button>
      </Grid>
    </Grid>
  );
};


UserDefined.propTypes = {
  className: PropTypes.string,
  isCPT: PropTypes.bool,
  fromLabOrder: PropTypes.bool,
  isDxUpdate: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  handleList: PropTypes.func,
  handleSaveDiagnosis: PropTypes.func,
  handleFieldChange: PropTypes.func,
  getValuesforCopy: PropTypes.func,
};


const mapDispatchToProps = {
  getUserICD10Codes,
  getSystemICD10Codes,
  addSystemCPTCode,
  deleteUserICD10Code,
  getUserCPTCodes,
  getSystemCPTCodes,
  addSystemICD10Code,
  addUserICD10Code,
  deleteUserCPTCode,
  addUserCPTCode,
  savePastDiagnosis,
  saveTempData,
};


// FIX: corrected state paths — state.providers was incorrectly guarding unrelated slices
const mapStateToProps = state => ({
  ehrID: state.profile?.ehrID,
  userID: state.profile?.userID,
  userICD10: state.icd10?.userICD10,
  systemICD10: state.icd10?.systemICD10,
  userCPT: state.cpt?.userCPT,
  systemCPT: state.cpt?.systemCPT,
});


export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true
})(withStyles(styles)(UserDefined));